import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Button, useMediaQuery, useTheme } from '@mui/material';
import { useContext, useMemo } from 'react';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';

import fuzzysort from 'fuzzysort';

import type { TranscriptionResponseDTO } from '@klangio/ai2notes-ts-client';
import type { ITheme } from '../../../../theme';
import type { AprioriTranscriptionParameters } from '../../../../types';

import { TranscriptionStoreContext } from '../../../../context/TranscriptionStoreContext/TranscriptionStoreContext';
import useLocaleNavigate from '../../../../hooks/useLocalNavigation';
import SongbookItemIcon from './SongbookItemIcon';
import { safeJSONParse } from '../../../../utils';

const useStyles = makeStyles<void>()((theme: ITheme) => ({
  arrowButton: {
    gridArea: 'arrow',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: '1em',
  },
  row: {
    color: '#fff',
    textDecoration: 'none',
    display: 'grid',
    gridTemplateAreas: `
                "icon title title arrow"\n
                "icon composer composer arrow"\n
            `,
    gridTemplateColumns: '1fr 10fr 1fr',
    gridTemplateRows: 'auto 1fr',
    paddingTop: '6px',
    paddingBottom: '6px',
    cursor: 'pointer',
    position: 'relative',
    gap: 0,
    borderRadius: '12px',
    margin: 0,
    width: '100%',
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.39) !important',
    },
  },
  title: {
    gridArea: 'title',
    lineHeight: '1.5',
    fontWeight: theme.typography.fontWeightMedium,
    marginLeft: 10,
  },
  composer: {
    gridArea: 'composer',
    color: '#ffffff82',
    fontSize: '13px',
    marginLeft: 10,
  },
  gridCell: {
    alignItems: 'center',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2, // max. 2 lines, than ellipsis
    WebkitBoxOrient: 'vertical',
    WebkitBoxAlign: 'start' as const,
  },
  icon: {
    gridArea: 'icon',
    fontSize: '1rem',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'visible',
  },
}));

export interface SongbookItemProps {
  transcription: TranscriptionResponseDTO;
  searchResult?: Fuzzysort.KeyResult<TranscriptionResponseDTO>;
}

const SongbookItem: React.FC<SongbookItemProps> = ({ transcription, searchResult }) => {
  const theme = useTheme();
  const { navigateTo, localeUrl } = useLocaleNavigate();
  const { classes } = useStyles();
  // eslint-disable-next-line max-len
  const { setSelectedTranscription, selectedTranscription, setShowSelectedOnMobile } =
    useContext(TranscriptionStoreContext);
  const selected = selectedTranscription?.id === transcription.id;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Memoized data
  const title = useMemo(() => {
    let transcriptionTitle: string | (string | JSX.Element)[] = transcription.title;
    // highlight if fuzzysort search result is available => highlight
    if (searchResult) {
      transcriptionTitle = fuzzysort.highlight(searchResult, (m, i) => <b key={i}>{m}</b>);
    }
    if (!transcriptionTitle || transcriptionTitle === '') {
      const parsed = safeJSONParse(transcription?.parameters) as AprioriTranscriptionParameters;
      transcriptionTitle = parsed?.apriori?.title ?? transcription?.title;
    }
    return transcriptionTitle;
  }, [transcription, searchResult]);

  const composer = useMemo(() => {
    let transcriptionComposer = transcription.composer;
    if (!transcriptionComposer) {
      const parsed = safeJSONParse(transcription?.parameters) as AprioriTranscriptionParameters;
      transcriptionComposer = parsed?.apriori?.composer ?? transcription?.composer;
    }
    return transcriptionComposer;
  }, [transcription]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const isNewTab = event.metaKey || event.ctrlKey || event.altKey || event.button === 1;
    const url = localeUrl(`/file?id=${transcription.id}`);

    if (selected) {
      if (isNewTab) {
        window.open(url, '_blank');
      } else {
        navigateTo(`/file?id=${transcription.id}`);
      }
      return;
    }
    if (isNewTab) {
      window.open(url, '_blank');
      return;
    }

    setSelectedTranscription(transcription);
    setShowSelectedOnMobile(true);
  };

  const handleDoubleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (selectedTranscription?.id === transcription.id) {
      navigateTo(`/file?id=${selectedTranscription?.id}`);
    }
  };

  const icon = useMemo(() => <SongbookItemIcon transcription={transcription} />, [transcription]);
  return (
    <Button
      style={{
        borderRadius: '10px',
        border: selected && !isMobile ? `1px solid ${theme.palette.primary.main}` : 'none',
        backgroundColor: selected && !isMobile ? '#00000063' : 'transparent',
      }}
      data-testid={`songbook-item-${transcription.id}`}
      className={classes.row}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}>
      <div className={`${classes.icon} ${classes.gridCell}`}>{icon}</div>
      <div className={`${classes.title} ${classes.gridCell}`}>{title}</div>
      <div className={`${classes.composer} ${classes.gridCell}`}>{composer ?? ''}</div>
      <div className={classes.arrowButton}>
        <KeyboardArrowRightIcon />
      </div>
    </Button>
  );
};

export default SongbookItem;
